import React from "react";
import {
  useBackgroundJobStatusQuery,
  type BackgroundJobStatusQuery,
} from "~/graphql/generated/hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@components/ui/card";
import { Skeleton } from "@components/ui/skeleton";
import {
  JOB_STATUS_CARDS_POLL_INTERVAL_MS,
  formatDuration,
} from "./jobStatusFormat";
import { Activity, Clock, ListTodo } from "lucide-react";

type BackgroundJobStatus = BackgroundJobStatusQuery["backgroundJobStatus"];

export const JobStatusCards: React.FC = () => {
  const { data, loading, error } = useBackgroundJobStatusQuery({
    fetchPolicy: "cache-and-network",
    pollInterval: JOB_STATUS_CARDS_POLL_INTERVAL_MS,
  });

  if (error) {
    return (
      <Card className="border-destructive/30 bg-destructive/5">
        <CardContent className="pt-6">
          <p className="text-sm text-destructive">
            Unable to load background job status.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (loading && !data?.backgroundJobStatus) {
    return <JobStatusCardsSkeleton />;
  }

  if (!data?.backgroundJobStatus) {
    return null;
  }

  return <JobStatusCardsContent status={data.backgroundJobStatus} />;
};

const JobStatusCardsContent: React.FC<{ status: BackgroundJobStatus }> = ({
  status,
}) => {
  const runningCount = status.predictionBacklog.runningPredictionCount;
  const queuedCount = status.predictionBacklog.queuedPredictionCount;
  const workerSlots = status.predictionBacklog.effectiveWorkerSlots;
  const estimatedWaitSeconds = status.predictionBacklog.estimatedWaitSeconds;
  const averageDurationSeconds =
    status.predictionBacklog.averagePredictionDurationSeconds;
  const hasPredictionActivity = runningCount > 0 || queuedCount > 0;
  const workerCapacity = Math.max(workerSlots, 0);
  const workerSaturated =
    workerCapacity > 0 && runningCount >= workerCapacity;
  const currentWaitTime =
    queuedCount === 0 && !workerSaturated
      ? "0s"
      : estimatedWaitSeconds !== null && estimatedWaitSeconds !== undefined
        ? formatDuration(estimatedWaitSeconds)
        : hasPredictionActivity
          ? "Pending first completed job"
          : "No queued tests";

  return (
    <Card className="gap-4 py-4">
      <CardHeader className="px-5">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Activity className="size-4 text-muted-foreground" />
          Prediction queue status
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-5 px-5 lg:grid-cols-[1fr_1fr_1.2fr] lg:gap-0">
        <section className="flex items-center justify-center lg:pr-6">
          <WorkerUtilisationChart
            runningCount={runningCount}
            workerSlots={workerSlots}
          />
        </section>

        <section className="border-t pt-5 lg:border-l lg:border-t-0 lg:px-6 lg:pt-0">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <Clock className="size-4 text-muted-foreground" />
            Prediction queue
          </div>
          <div className="grid grid-cols-2 gap-3">
            <MetricTile label="Processing" value={runningCount} />
            <MetricTile label="Queued" value={queuedCount} />
          </div>
        </section>

        <section className="border-t pt-5 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <ListTodo className="size-4 text-muted-foreground" />
            Prediction wait
          </div>
          <div className="grid gap-3">
            <div>
              <div className="text-sm text-muted-foreground">
                Current wait time for new tests
              </div>
              <div className="mt-1 text-3xl font-semibold tabular-nums">
                {currentWaitTime}
              </div>
            </div>
            {averageDurationSeconds !== null &&
              averageDurationSeconds !== undefined && (
                <MetricRow
                  label="Average time per test"
                  value={formatDuration(averageDurationSeconds)}
                />
              )}
          </div>
        </section>
      </CardContent>
    </Card>
  );
};

const MetricRow: React.FC<{ label: string; value: number | string }> = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between gap-3 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

const WorkerUtilisationChart: React.FC<{
  runningCount: number;
  workerSlots: number;
}> = ({ runningCount, workerSlots }) => {
  const totalSlots = Math.max(workerSlots, 0);
  const busySlots = Math.min(Math.max(runningCount, 0), totalSlots);
  const centre = 40;
  const radius = 30;
  const segmentGapDegrees = totalSlots > 1 ? 20 : 0;

  if (totalSlots === 0) {
    return (
      <div
        className="grid grid-cols-[auto_1fr] items-center gap-4"
        aria-label="Prediction worker utilisation: 0 slots configured"
        role="img"
      >
        <div className="grid size-24 place-items-center rounded-full border-[10px] border-muted-foreground/30 text-center">
          <div>
            <div className="text-xl font-semibold tabular-nums">0</div>
            <div className="text-[10px] text-muted-foreground">slots</div>
          </div>
        </div>

        <div className="text-xs text-muted-foreground">
          No prediction workers configured
        </div>
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-[auto_1fr] items-center gap-4"
      aria-label={`Prediction worker utilisation: ${busySlots} of ${totalSlots} slots busy`}
      role="img"
    >
      <div className="relative size-24">
        <svg className="size-24" viewBox="0 0 80 80">
          {Array.from({ length: totalSlots }, (_, index) => {
            const segmentDegrees = 360 / totalSlots;
            const startAngle =
              -90 + index * segmentDegrees + segmentGapDegrees / 2;
            const endAngle =
              -90 + (index + 1) * segmentDegrees - segmentGapDegrees / 2;
            const isBusy = index < busySlots;

            return (
              <path
                key={index}
                d={describeArcSegment(
                  centre,
                  centre,
                  radius,
                  startAngle,
                  endAngle
                )}
                className={
                  isBusy
                    ? "fill-none stroke-primary"
                    : "fill-none stroke-muted-foreground/30"
                }
                strokeLinecap="round"
                strokeWidth="10"
              />
            );
          })}
        </svg>
        <div className="absolute inset-0 grid place-items-center text-center">
          <div>
            <div className="text-xl font-semibold tabular-nums">
              {busySlots}/{totalSlots}
            </div>
            <div className="text-[10px] text-muted-foreground">busy</div>
          </div>
        </div>
      </div>

      <div className="grid gap-2 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2 rounded-sm bg-primary" />
          Busy
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2 rounded-sm bg-muted-foreground/30" />
          Idle
        </span>
      </div>
    </div>
  );
};

const describeArcSegment = (
  centreX: number,
  centreY: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string => {
  const start = polarToCartesian(centreX, centreY, radius, startAngle);
  const end = polarToCartesian(centreX, centreY, radius, endAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
};

const polarToCartesian = (
  centreX: number,
  centreY: number,
  radius: number,
  angleInDegrees: number
): { x: number; y: number } => {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;

  return {
    x: centreX + radius * Math.cos(angleInRadians),
    y: centreY + radius * Math.sin(angleInRadians),
  };
};

const MetricTile: React.FC<{ label: string; value: number }> = ({
  label,
  value,
}) => (
  <div className="rounded-md bg-muted px-4 py-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    <div className="mt-1 text-3xl font-semibold tabular-nums">{value}</div>
  </div>
);

const JobStatusCardsSkeleton: React.FC = () => (
  <Card className="gap-4 py-4">
    <CardHeader className="px-5">
      <Skeleton className="h-4 w-44" />
    </CardHeader>
    <CardContent className="grid gap-5 px-5 lg:grid-cols-3">
      {[0, 1, 2].map((item) => (
        <div key={item} className="grid gap-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-20 w-full" />
        </div>
      ))}
    </CardContent>
  </Card>
);
